import {
  breakpoint,
  legacyDimensions,
  overlayPosition,
  palette,
  typography,
} from './tokens';

const legacyTheme = {
  primaryColor: palette.coolBlack,
  disabledColor: palette.coolBlack,
  fontFamily: 'Helvetica, sans-serif',
  secondaryColor: palette.coolBlack,
  promotionColor: palette.red,
  trendColor: palette.orangeYellow,
  trendFontSize: '12rem',
  trendRadiusSize: '12rem',
  borderColor: palette.platinum,
  layoutBackgroundColor: palette.athensGray,
  eeHeaderColor: palette.darkJungleGreen,
  eeWhite: palette.white,
  eeBlack: palette.blueCharcoal,
  cardColor: palette.dimGray,
  textColor: palette.gray,
  inputBorderColor: palette.lightGray,
  cardBackgroundColor: palette.white,
  shadowColor: `rgba(${palette.sonicSilver}, 0.2)`,
  shadowPriceDetailsColor: `rgba(${palette.silverSand}, 0.5)`,
  shadowConfirmationModalColor: `rgba(${palette.black}, 0.2)`,
  shadowCustomSidesColor: `rgba(${palette.black}, 0.161)`,
  applianceCardShadow: `rgba(${palette.black}, 0.075)`,
  applianceCardShadowHover: `rgba(${palette.black}, 0.15)`,
  eeMenuBackground: palette.concrete,
  eeScrollbarColor: palette.catskillWhite,
  hyperlinkColor: palette.blueCola,
  titleMaterialSelected: palette.coolBlack,
  eeErrorColorLight: palette.red,
  eeErrorColor: palette.cadmiumRed,
  eeWarningColor: palette.chineseYellow,
  eeSuccessColor: palette.northTexasGreen,
  eeSubTitleColor: palette.darkJungleGreen,
  eeCardDescriptionColor: palette.midGray,
  eeDescriptionColor: palette.sonicSilver,
  eeBorderColor: palette.lightGray,
  eeDividerColor: palette.lightGray,
  eeTextColor: palette.blueCharcoal,
  eeContrastTextColor: palette.sonicSilver,
  eeDisabledButtonColor: palette.quickSilver,
  eeSavingsColor: palette.green,
  eePromotionEndDateColor: palette.red,
  eeTooltipColor: palette.coolBlack,
  eeTooltipTextColor: palette.white,
  eeLineItemColorDark: `rgba(${palette.concrete}, 0)`,
  eeGreyColor: palette.silverSand,
  eeLinkColor: palette.tuftsBlue,
  eeGray: palette.gray,
  eeRatingIconFillColor: palette.deepJungleGreen,
  eeConfirmationDialogBackground: `linear-gradient(180deg, ${palette.white}, ${palette.linkWater})`,
};

const lightTheme = {
  header: {
    primary: {
      text: palette.oldSilver,
      bg: palette.darkLiver,
      textHover: palette.desertSand,
      textActive: palette.desertSand,
      border: palette.richBlack,
    },
    primaryAccent: {
      text: palette.white,
      bg: palette.oldSilver,
      textHover: palette.darkLiver,
      bgHover: palette.desertSand,
      textActive: palette.darkLiver,
      bgActive: palette.desertSand,
    },
    secondary: {
      text: palette.desertSand,
      bg: palette.darkGray,
      border: palette.superLightGray,
    },
    secondaryAccent: {
      text: palette.white,
    },
  },
  card: {
    primary: {
      text: palette.darkLiver,
      bg: palette.white,
      shadow: 'rgb(0 0 0 / 0.29)',
      shadowHover: 'rgb(0 0 0 / 0.4)',
      active: palette.darkLiver,
      focus: palette.artichoke,
      ratingStars: palette.erieBlack,
      fontFamily: typography.family.arial,
    },
  },
  filter: {
    primary: {
      bg: palette.white,
      text: palette.black,
      bgActive: palette.black,
      textDisabled: palette.lightGray,
      bgDisabled: palette.lightGray,
      border: palette.gainsboro,
    },
    secondary: {
      border: palette.silverFoil,
    },
  },
  page: {
    nextStepBtn: {
      bg: palette.white,
      text: palette.darkLiver,
      bgHover: palette.darkLiver,
      textHover: palette.white,
      borderFocus: palette.artichoke,
    },

    title: {
      text: palette.artichoke,
    },

    subtitle: {
      text: palette.darkLiver,
    },

    input: {
      backgroundColor: palette.white,
      borderColor: palette.gainsboro,
    },

    primary: {
      text: palette.erieBlack,
    },
  },
  drawingTool: {
    drawingShape: {
      stroke: {
        active: palette.black,
        normal: palette.gray,
      },
    },
    fontFamily: 'Helvetica, sans-serif',
    borderColor: palette.platinum,
    secondaryBorderColor: palette.coolBlack,
  },
} as const;

/**
 * theme definition
 *  "section name" [header|design|slider|page]
 *    - [primary|secondary|etc] | category name
 *      - [text|bg]                         | without interactions
 *      - [text|bg]Hover                    | on hovering state (ex: cursor)
 *      - [text|bg]Focus                    | on focus state (ex: keyboard)
 *      - [text|bg]Active                   | on pressed/toggled/activated state
 *      - [text|bg]Disable                  | on disabled/unable state
 *      - border[Top|Bottom|Left|Right|X|Y] | if the section has border/shadow
 *    - <category name>Accent | variation
 *  palette
 *    - [colorName]
 *  typography
 *    - family
 *      - [familyName]
 *    - size
 *      - [sizeCategory large|normal|small]
 *  breakpoint
 *    - screen
 *      - [sm|md|lg|xl]
 *    - prose
 *  legacyDimensions
 *    - [dimension property]
 *  defaultTheme                            | access to legacy theme
 *    - [legacy theme property]
 */
const baseTheme = {
  palette,
  typography,
  breakpoint,
  legacyDimensions,
  overlayPosition,
  defaultTheme: legacyTheme,
};

export type ThemeContract = typeof lightTheme & typeof baseTheme;

const theme = {
  light: {
    ...lightTheme,
    ...baseTheme,
  },
} as const;

export default theme;

export type Theme = keyof typeof theme;
